import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useGetEnrollmentPortfolioSettingsData, useSubmitEnrollmentPortfolioSettingsData } from '@egw/api'
import { Button, GenericError, RangeGraphV2, Collapse, ListRow } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentLayout } from '@egw/layouts'
import { useRouter } from '@egw/router'
import {
  type CashEquivalentCategory,
  type InvestmentRiskToleranceCategory,
  type EquitySectorAllocationInput,
  type FixedIncomeSectorAllocationInput,
  type EnrollmentFixedIncomeRecommendationOutput,
  type EnrollmentEquitySectorRecommendationOutput,
} from '@egw/api/src/models/enrollment/enrollment'
import { PageHeader } from '../../../components'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { RiskReturnAssetAllocation } from '../../../components/risk-return-asset-allocation/risk-return-asset-allocation'
import { ModalEquity } from '../../../components/modal-equitity/modal-equity'
import { ModalFixedIncome } from '../../../components/modal-fixed-income/modal-fixed-income'
import { ModalCashEquivalent } from '../../../components/modal-cash-equivalent/modal-cash-equivalent'
import { ModalRiskTolerance, type RiskTolerance } from '../../../components/modal-risk-tolerance/modal-risk-tolerance'

const log = new Logger('Enrollment - Portfolio Settings')

interface PortfolioData {
  riskTolerance: RiskTolerance | undefined
  equity: EnrollmentEquitySectorRecommendationOutput[] | undefined
  fixedIncome: EnrollmentFixedIncomeRecommendationOutput[] | undefined
  cashEquivalent: CashEquivalentCategory | undefined
}

export const PortfolioSettingsPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()

  const { data, isLoading, error } = useGetEnrollmentPortfolioSettingsData()
  const { mutate: submit, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentPortfolioSettingsData()

  const experience = data?.enrollmentData.experimentId

  const initRecommendedRiskToleranceCategory =
    data?.enrollmentData.recommendation.investment.riskTolerance.recommendedRiskTolerance
  const allowedRiskTolerances = data?.enrollmentData.recommendation.investment.riskTolerance.allowedRiskTolerances
  const riskTolerances = data?.enrollmentData.recommendation.investment.riskTolerance.riskTolerances

  const riskTolerance = data?.enrollmentData.userInfo.investment.riskTolerance
  const equitySectorAllocations = data?.enrollmentData.userInfo.investment.equitySectorAllocations
  const fixedIncomeSectorAllocations = data?.enrollmentData.userInfo.investment.fixedIncomeSectorAllocations
  const cashEquivalentData = data?.enrollmentData.userInfo.investment.cashEquivalent

  const recommendedRiskTolerance = useMemo(() => {
    const initRecommended = riskTolerances?.find((item) => item.category === initRecommendedRiskToleranceCategory)

    return {
      ...riskTolerances?.find((item) => item.category === riskTolerance?.type),
      initIndex: initRecommended?.index,
    }
  }, [riskTolerances, initRecommendedRiskToleranceCategory, riskTolerance?.type])

  const initData = useRef<PortfolioData>({
    riskTolerance: recommendedRiskTolerance as RiskTolerance,
    equity: equitySectorAllocations,
    fixedIncome: fixedIncomeSectorAllocations,
    cashEquivalent: cashEquivalentData?.current,
  })

  const [portfolioData, setPortfolioData] = useState<PortfolioData>({
    riskTolerance: recommendedRiskTolerance as RiskTolerance,
    equity: equitySectorAllocations,
    fixedIncome: fixedIncomeSectorAllocations,
    cashEquivalent: cashEquivalentData?.current,
  })

  const [activeModal, setActiveModal] = useState<'risk' | 'equity' | 'fixed' | 'cash' | null>(null)

  const isContinueDisabled = useMemo(() => {
    return Object.values(portfolioData).some((value) => !value)
  }, [portfolioData])

  const updatePortfolioData = useCallback((key: keyof PortfolioData, value: unknown) => {
    setPortfolioData((prev) => ({
      ...prev,
      [key]: value,
    }))
  }, [])

  // Get Title
  const getTitle = useMemo(() => {
    const category = riskTolerances?.find((item) => item.index === portfolioData.riskTolerance?.index)?.category
    return category
      ? category
          .split('_')[0]
          .toLowerCase()
          .replace(/^\w/, (c) => c.toUpperCase())
      : ''
  }, [portfolioData.riskTolerance?.index, riskTolerances])

  // Protect route
  useValidateRoute(data?.path)

  const handleLinkClick = async (): Promise<void> => {
    try {
      const response = await submit({
        investmentProfile: {
          riskTolerance: portfolioData.riskTolerance?.category as InvestmentRiskToleranceCategory,
          equitySectorAllocation: portfolioData.equity?.map((item) => ({
            equitySector: item.type,
            allocationPercent: item.currentAllocationPercent ?? item.recommendedRange.recommendedPercent,
          })) as EquitySectorAllocationInput[],
          fixedIncomeCategoryAllocation: portfolioData.fixedIncome?.map((item) => ({
            fixedIncomeCategory: item.type,
            allocationPercent: item.currentAllocationPercent ?? item.recommendedRange.recommendedPercent,
          })) as FixedIncomeSectorAllocationInput[],
          cashEquivalentCategory: portfolioData.cashEquivalent!,
        },
      })
      if (response?.path) {
        navigate(response.path)
      }
    } catch (err) {
      log.error('Error submitting enrollment intro data', err)
    }
  }

  const handleCloseModal = (): void => {
    setActiveModal(null)
  }

  useEffect(() => {
    if (
      recommendedRiskTolerance ||
      fixedIncomeSectorAllocations ||
      equitySectorAllocations ||
      fixedIncomeSectorAllocations ||
      cashEquivalentData
    ) {
      const newData = {
        riskTolerance: recommendedRiskTolerance as RiskTolerance,
        equity: equitySectorAllocations,
        fixedIncome: fixedIncomeSectorAllocations,
        cashEquivalent: cashEquivalentData?.current,
      }

      setPortfolioData(newData)
      initData.current = newData
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendedRiskTolerance, fixedIncomeSectorAllocations, equitySectorAllocations, fixedIncomeSectorAllocations])

  return (
    <EnrollmentLayout>
      <div className="egw-container-enroll h-full flex flex-col">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error && !submitError ? (
          <>
            <PageHeader title={t('investments.portfolio-settings.title')} />
            <p className="typo-primary-sm-regular text-text-meta">{t('investments.portfolio-settings.description')}</p>
            <p className="mt-xl typo-primary-sm-semibold text-text-meta">
              {t('investments.portfolio-settings.risk_tolerance')}
            </p>
            <p className="mb-xl typo-primary-xl-semibold text-sky-400" data-testid="risk-tolerance-title">
              {getTitle}
            </p>
            <p className="typo-primary-sm-semibold text-text-meta mb-sm mt-sm">
              {t('investments.portfolio-settings.asset_allocation')}
            </p>
            <RiskReturnAssetAllocation currentRiskTolerance={portfolioData.riskTolerance?.allocation} />
            <p className="typo-primary-sm-semibold text-text-meta -mb-md mt-xl">
              {t('investments.portfolio-settings.expected_return')}
            </p>
            <RangeGraphV2 value={portfolioData.riskTolerance?.index ?? 0} style={{ width: '99%', height: '260px' }} />
            <Collapse
              title={t('investments.portfolio-settings.adjust_portfolio')}
              className="typo-primary-base-bold border-y py-md border-borders-light text-text-header mt-xl"
            >
              <ListRow
                onClick={() => {
                  setActiveModal('risk')
                }}
                data-testid="risk-tolerance-row"
                title={t('investments.portfolio-settings.risk_tolerance_adjust')}
                hasBorder
              />
              <ListRow
                onClick={() => {
                  setActiveModal('equity')
                }}
                data-testid="equity-adjust-row"
                title={t('investments.portfolio-settings.equity_adjust')}
                hasBorder
              />
              <ListRow
                onClick={() => {
                  setActiveModal('fixed')
                }}
                data-testid="fixed-income-adjust-row"
                title={t('investments.portfolio-settings.fixed_income_adjust')}
                hasBorder
              />
              <ListRow
                onClick={() => {
                  setActiveModal('cash')
                }}
                data-testid="cash-equivalent-adjust-row"
                title={t('investments.portfolio-settings.cash_equivalent_adjust')}
                hasBorder
              />
            </Collapse>

            <ModalRiskTolerance
              isOpen={activeModal === 'risk'}
              onClose={handleCloseModal}
              handleContinueClick={() => {
                handleCloseModal()
                initData.current.riskTolerance = portfolioData.riskTolerance
              }}
              riskToleranceData={portfolioData.riskTolerance}
              setRiskToleranceData={(value) => {
                updatePortfolioData('riskTolerance', value)
              }}
              allowedRiskTolerances={allowedRiskTolerances}
              riskTolerances={riskTolerances ?? []}
              recommendedValue={recommendedRiskTolerance as RiskTolerance}
              initRiskToleranceData={initData.current.riskTolerance}
              title={getTitle}
              experience={experience}
            />

            <ModalEquity
              isOpen={activeModal === 'equity'}
              onClose={handleCloseModal}
              handleContinueClick={() => {
                handleCloseModal()
                initData.current.equity = portfolioData.equity
              }}
              riskTolerance={portfolioData.riskTolerance}
              initEquityData={initData.current.equity}
              setEquityData={(updater) => {
                if (typeof updater === 'function') {
                  updatePortfolioData('equity', updater(portfolioData.equity))
                } else {
                  updatePortfolioData('equity', updater)
                }
              }}
              sliderData={portfolioData.equity}
              experience={experience}
            />

            <ModalFixedIncome
              isOpen={activeModal === 'fixed'}
              onClose={handleCloseModal}
              handleContinueClick={() => {
                handleCloseModal()
                initData.current.fixedIncome = portfolioData.fixedIncome
              }}
              riskTolerance={portfolioData.riskTolerance}
              initEquityData={initData.current.fixedIncome}
              setData={(updater) => {
                if (typeof updater === 'function') {
                  updatePortfolioData('fixedIncome', updater(portfolioData.fixedIncome))
                } else {
                  updatePortfolioData('fixedIncome', updater)
                }
              }}
              sliderData={portfolioData.fixedIncome}
              experience={experience}
            />

            <ModalCashEquivalent
              isOpen={activeModal === 'cash'}
              onClose={handleCloseModal}
              handleContinueClick={(value: CashEquivalentCategory) => {
                handleCloseModal()
                initData.current.cashEquivalent = value
              }}
              riskTolerance={portfolioData.riskTolerance}
              cashEquivalentData={cashEquivalentData}
              setCashEquivalentData={(value) => {
                updatePortfolioData('cashEquivalent', value)
              }}
              initData={initData.current.cashEquivalent}
            />

            <Button
              type="button"
              className="w-full md:w-min mt-lg"
              testId="enrollment-continue-button"
              isLoading={isSubmitting}
              isDisabled={isContinueDisabled}
              onClick={() => {
                void handleLinkClick()
              }}
            >
              {t('common.continue')}
            </Button>
          </>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {(error ?? submitError) ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentLayout>
  )
}
